import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useAuth } from "../../auth/AuthProvider";
import { normalizeClaims } from "../../auth/landing";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import { recordMcTrafficFirestoreRead, recordMcTrafficFirestoreWrite, recordMcTrafficWriteAttempt, recordMcTrafficWriteFailure } from "../utils/mcTrafficProbe";
import "./mcTickets.css";

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(value) {
  const dt = toDate(value);
  if (!dt) return "—";
  return new Intl.DateTimeFormat("en-ZA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

function statusClass(status) {
  const key = String(status || "").toLowerCase();
  if (key === "open") return "is-open";
  if (key === "in progress") return "is-progress";
  if (key === "waiting on client" || key === "waiting") return "is-waiting";
  if (key === "resolved") return "is-resolved";
  if (key === "closed") return "is-closed";
  return "";
}

function priorityClass(priority) {
  const key = String(priority || "").toLowerCase();
  if (key === "critical") return "is-critical";
  if (key === "high") return "is-high";
  if (key === "normal") return "is-normal";
  if (key === "low") return "is-low";
  return "";
}

function starsView(value) {
  const n = Math.max(0, Math.min(5, Number(value || 0)));
  return "★".repeat(Math.floor(n)) + "☆".repeat(5 - Math.floor(n));
}

function Pill({ children, className = "" }) {
  return <span className={["mc-ticket-pill", className].filter(Boolean).join(" ")}>{children}</span>;
}

function StatCard({ label, value, tone }) {
  return (
    <article className={["mc-ticket-stat", tone].filter(Boolean).join(" ")}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

const AREA_TOPIC_OPTIONS = {
  General: ["Access or permission issue", "Navigation issue", "General bug", "How to", "Other"],
  Dashboard: ["Dashboard count issue", "Wrong metrics", "Filter issue", "Other"],
  Import: ["CSV upload issue", "Import count issue", "Raw data issue", "Other"],
  "Prepared Data": ["Prepared rows issue", "Duplicate merge issue", "Wrong site data", "Other"],
  Sessions: ["Live count issue", "Finalize count issue", "Session missing", "Other"],
  Archives: ["Archive missing", "Restore issue", "Completed session issue", "Other"],
  Sites: ["Site activation issue", "Billable site issue", "Site access issue", "Other"],
  Profiles: ["PIN/profile issue", "Profile site access", "Admin profile issue", "Other"],
  Billing: ["MC billing count issue", "Rate/status issue", "Pay Now issue", "Other"],
  Config: ["Threshold setting issue", "MOS PDF setting", "Cannot save config", "Other"],
  Mapping: ["Column mapping issue", "System SOH mapping", "All Sites mapping", "Other"],
  Categories: ["Category grouping issue", "Rule issue", "Ordering issue", "Other"],
};


function ticketTrafficRaw(value) {
  try { return JSON.stringify(value ?? {}); } catch (_) { return ""; }
}

function recordTicketListenerSnapshot(prefix, snap) {
  const meta = {
    fromCache: snap?.metadata?.fromCache === true,
    hasPendingWrites: snap?.metadata?.hasPendingWrites === true,
  };
  (snap?.docs || []).forEach((row) => {
    const data = row?.data?.() || {};
    recordMcTrafficFirestoreRead(`${prefix}:${row?.id || "unknown"}`, ticketTrafficRaw(data), "listener", { ...meta, documentData: data });
  });
}

async function trackedTicketAddDoc(ref, prefix, payload) {
  recordMcTrafficWriteAttempt(prefix, 1);
  try {
    const created = await addDoc(ref, payload);
    const key = `${prefix}:${created?.id || "new"}`;
    recordMcTrafficFirestoreWrite(key, ticketTrafficRaw(payload), { documentData: payload });
    return created;
  } catch (error) {
    recordMcTrafficWriteFailure(prefix, { phase: "write", attempt: 1 });
    throw error;
  }
}

async function trackedTicketUpdateDoc(ref, key, payload) {
  recordMcTrafficWriteAttempt(key, 1);
  try {
    await updateDoc(ref, payload);
    recordMcTrafficFirestoreWrite(key, ticketTrafficRaw(payload), { documentData: payload });
  } catch (error) {
    recordMcTrafficWriteFailure(key, { phase: "write", attempt: 1 });
    throw error;
  }
}

function buildTicketNumber() {
  const d = new Date();
  const y = String(d.getFullYear()).slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `MC-${y}${m}${day}-${rand}`;
}

export function MorpheusCountTicketsContent() {
  const { user, claims } = useAuth();
  const norm = normalizeClaims(claims || {});
  const orgId = norm.orgId || null;

  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({ area: "General", topic: "", priority: "Normal", description: "" });

  useEffect(() => {
    if (!orgId) return undefined;
    const q = query(collection(db, "orgs", orgId, "supportTickets"));
    const unsub = onSnapshot(q, (snap) => {
      recordTicketListenerSnapshot("tickets:list", snap);
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((ticket) => {
          const source = String(ticket.sourceApp || ticket.app || "").toLowerCase();
          return source === "morpheus_count" || source === "mc" || source === "morpheus" || !source;
        })
        .sort((a, b) => {
          const ad = toDate(a.updatedAt || a.createdAt)?.getTime() || 0;
          const bd = toDate(b.updatedAt || b.createdAt)?.getTime() || 0;
          return bd - ad;
        });
      setTickets(rows);
      setSelectedId((prev) => (prev && rows.some((x) => x.id === prev) ? prev : rows[0]?.id || ""));
    });
    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !selectedId) {
      setMessages([]);
      return undefined;
    }
    const q = query(
      collection(db, "orgs", orgId, "supportTickets", selectedId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        recordTicketListenerSnapshot(`tickets:messages:${selectedId}`, snap);
        setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      },
      () => setMessages([])
    );
    return () => unsub();
  }, [orgId, selectedId]);

  const filteredTickets = useMemo(() => {
    if (statusFilter === "all") return tickets;
    return tickets.filter((x) => String(x.status || "").toLowerCase() === statusFilter);
  }, [tickets, statusFilter]);

  const selectedTicket = useMemo(() => tickets.find((x) => x.id === selectedId) || null, [tickets, selectedId]);
  const selectedAreaTopics = useMemo(() => AREA_TOPIC_OPTIONS[createForm.area] || ["Other"], [createForm.area]);

  const hasSupportReply = useMemo(
    () => messages.some((message) => {
      const senderType = String(message.senderType || message.authorType || "").toLowerCase();
      const senderName = String(message.senderName || "").toLowerCase();
      return senderType === "support" || senderName.includes("invoice mate") || senderName.includes("matrixos");
    }),
    [messages]
  );

  const canReply = Boolean(selectedTicket) && hasSupportReply;

  useEffect(() => {
    async function markClientViewed() {
      if (!orgId || !selectedTicket) return;
      const lastReplyBy = String(selectedTicket.lastReplyBy || "").toLowerCase();
      if (lastReplyBy !== "support") return;
      try {
        await trackedTicketUpdateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), `tickets:item:${selectedTicket.id}`, {
          clientLastViewedAt: serverTimestamp(),
        });
      } catch {}
    }
    markClientViewed();
  }, [orgId, selectedTicket?.id, selectedTicket?.lastReplyBy]);

  const counts = useMemo(() => {
    const acc = { open: 0, inProgress: 0, waiting: 0, resolved: 0 };
    tickets.forEach((ticket) => {
      const key = String(ticket.status || "").toLowerCase();
      if (key === "open") acc.open += 1;
      else if (key === "in progress") acc.inProgress += 1;
      else if (key === "waiting on client" || key === "waiting") acc.waiting += 1;
      else if (key === "resolved") acc.resolved += 1;
    });
    return acc;
  }, [tickets]);

  async function handleCreateTicket() {
    if (!orgId || !user || createBusy) return;
    const topic = String(createForm.topic || "").trim();
    const description = String(createForm.description || "").trim();
    if (!topic || !description) {
      setError("Please complete the topic and description.");
      return;
    }
    setCreateBusy(true);
    setError("");
    try {
      const ticketNumber = buildTicketNumber();
      const ticketRef = await trackedTicketAddDoc(collection(db, "orgs", orgId, "supportTickets"), "tickets:item", {
        ticketNumber,
        orgId,
        orgName: norm.orgName || norm.organizationName || norm.org || "",
        area: createForm.area || "General",
        topic,
        priority: createForm.priority || "Normal",
        description,
        createdByUid: user.uid || "",
        createdByName: user.displayName || user.email || "Client",
        createdByEmail: user.email || "",
        sourceApp: "morpheus_count",
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastReplyAt: serverTimestamp(),
        lastReplyBy: "client",
      });

      await trackedTicketAddDoc(collection(db, "orgs", orgId, "supportTickets", ticketRef.id, "messages"), `tickets:messages:${ticketRef.id}`, {
        body: description,
        senderType: "client",
        senderUid: user?.uid || "",
        senderName: user?.displayName || user?.email || "Client",
        createdAt: serverTimestamp(),
      });

      setCreateOpen(false);
      setCreateForm({ area: "General", topic: "", priority: "Normal", description: "" });
      setSelectedId(ticketRef.id);
    } catch (err) {
      console.error(err);
      setError("Could not create your ticket right now.");
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleSendReply() {
    if (!orgId || !selectedTicket || !reply.trim() || !canReply || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await trackedTicketAddDoc(collection(db, "orgs", orgId, "supportTickets", selectedTicket.id, "messages"), `tickets:messages:${selectedTicket.id}`, {
        body: reply.trim(),
        senderType: "client",
        senderUid: user?.uid || "",
        senderName: user?.displayName || user?.email || "Client",
        createdAt: serverTimestamp(),
      });

      await trackedTicketUpdateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), `tickets:item:${selectedTicket.id}`, {
        status: "open",
        updatedAt: serverTimestamp(),
        lastReplyAt: serverTimestamp(),
        lastReplyBy: "client",
      });

      setReply("");
    } catch (err) {
      console.error(err);
      setError("Could not send your reply right now.");
    } finally {
      setSubmitting(false);
    }
  }

  async function markResolved() {
    if (!orgId || !selectedTicket || ratingBusy) return;
    setRatingBusy(true);
    setError("");
    try {
      await trackedTicketUpdateDoc(doc(db, "orgs", orgId, "supportTickets", selectedTicket.id), `tickets:item:${selectedTicket.id}`, {
        status: "resolved",
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setError("Could not update ticket status.");
    } finally {
      setRatingBusy(false);
    }
  }

  return (
    <div className="mc-tickets mc-tabPage">
      <div className="mc-tickets__grid" />
      <div className="mc-tickets__ambient mc-tickets__ambient--one" />
      <div className="mc-tickets__ambient mc-tickets__ambient--two" />

      <McPageHeader
        activeKey="tickets"
        actions={(
          <div className="mc-tickets__actions">
            <button type="button" className="mc-tickets__primary" onClick={() => { setCreateOpen(true); setError(""); }}>
              + New Ticket
            </button>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="in progress">In Progress</option>
              <option value="waiting on client">Waiting</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <div className="mc-tickets__count">{tickets.length} ticket{tickets.length === 1 ? "" : "s"}</div>
          </div>
        )}
      />

      <section className="mc-tickets__stats">
        <StatCard label="Open" value={counts.open} tone="is-open" />
        <StatCard label="In Progress" value={counts.inProgress} tone="is-progress" />
        <StatCard label="Waiting" value={counts.waiting} tone="is-waiting" />
        <StatCard label="Resolved" value={counts.resolved} tone="is-resolved" />
      </section>

      <section className="mc-tickets__layout">
        <aside className="mc-tickets__listPanel">
          <div className="mc-tickets__panelTitle">Ticket list</div>
          <div className="mc-tickets__list">
            {filteredTickets.length ? (
              filteredTickets.map((ticket) => {
                const selected = ticket.id === selectedId;
                const lastReplyBy = String(ticket.lastReplyBy || "").toLowerCase();
                const lastReplyAt = toDate(ticket.lastReplyAt);
                const viewedAt = toDate(ticket.clientLastViewedAt);
                const hasUnreadSupport = lastReplyBy === "support" && lastReplyAt && (!viewedAt || lastReplyAt > viewedAt);
                return (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => setSelectedId(ticket.id)}
                    className={["mc-tickets__item", selected ? "is-selected" : ""].filter(Boolean).join(" ")}
                  >
                    <div className="mc-tickets__itemTop">
                      <strong>{ticket.ticketNumber || ticket.reference || ticket.id}</strong>
                      <span className="mc-tickets__itemStatus">
                        {hasUnreadSupport ? <i /> : null}
                        <Pill className={statusClass(ticket.status)}>{ticket.status || "Open"}</Pill>
                      </span>
                    </div>
                    <div className="mc-tickets__itemTopic">{ticket.topic || ticket.subject || "Support ticket"}</div>
                    <div className="mc-tickets__itemMeta">{ticket.area || "General"} · {ticket.priority || "Normal"}</div>
                    {ticket?.rating?.stars ? <div className="mc-tickets__stars">{starsView(ticket.rating.stars)}</div> : null}
                    <small>Created {fmtDateTime(ticket.createdAt)}</small>
                  </button>
                );
              })
            ) : (
              <div className="mc-tickets__empty">No tickets found for this filter.</div>
            )}
          </div>
        </aside>

        <main className="mc-tickets__detailPanel">
          {selectedTicket ? (
            <>
              <div className="mc-tickets__detailHead">
                <div>
                  <div className="mc-tickets__eyebrow">Reference</div>
                  <h2>{selectedTicket.ticketNumber || selectedTicket.reference || selectedTicket.id}</h2>
                  <p>{selectedTicket.topic || selectedTicket.subject || "Support ticket"}</p>
                </div>
                <div className="mc-tickets__pills">
                  <Pill className={statusClass(selectedTicket.status)}>{selectedTicket.status || "Open"}</Pill>
                  <Pill className={priorityClass(selectedTicket.priority)}>{selectedTicket.priority || "Normal"}</Pill>
                </div>
              </div>

              <div className="mc-tickets__infoGrid">
                <div><span>Area</span><strong>{selectedTicket.area || "General"}</strong></div>
                <div><span>Created</span><strong>{fmtDateTime(selectedTicket.createdAt)}</strong></div>
                <div><span>Last updated</span><strong>{fmtDateTime(selectedTicket.updatedAt || selectedTicket.lastReplyAt || selectedTicket.createdAt)}</strong></div>
              </div>

              <section className="mc-tickets__box">
                <span>Original request</span>
                <p>{selectedTicket.description || selectedTicket.message || "—"}</p>
              </section>

              <section className="mc-tickets__conversation">
                <h3>Conversation</h3>
                {messages.length ? messages.map((message) => {
                  const senderType = String(message.senderType || message.authorType || "").toLowerCase();
                  const senderName = String(message.senderName || "").trim() || "Support";
                  const isSupport = senderType === "support" || senderName.toLowerCase().includes("invoice mate") || senderName.toLowerCase().includes("matrixos");
                  return (
                    <article key={message.id} className={["mc-tickets__message", isSupport ? "is-support" : "is-client"].join(" ")}>
                      <div>
                        <strong>{senderName}</strong>
                        <small>{fmtDateTime(message.createdAt)}</small>
                      </div>
                      <p>{message.body || "—"}</p>
                    </article>
                  );
                }) : <div className="mc-tickets__empty">No messages yet.</div>}
              </section>

              {selectedTicket?.rating?.stars ? (
                <section className="mc-tickets__rating">
                  <div><span>Your rating</span><strong>{starsView(selectedTicket.rating.stars)}</strong></div>
                  {selectedTicket?.rating?.comment ? <p>{selectedTicket.rating.comment}</p> : null}
                </section>
              ) : null}

              <section className="mc-tickets__reply">
                {!hasSupportReply ? (
                  <div className="mc-tickets__notice">Reply opens only after MatrixOS support has replied, to prevent abuse of the chat box.</div>
                ) : null}
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  disabled={!canReply || submitting}
                  placeholder={hasSupportReply ? "Write a reply to support..." : "You can reply here once MatrixOS support has replied."}
                />
                {error ? <div className="mc-tickets__error">{error}</div> : null}
                <div className="mc-tickets__replyActions">
                  <button type="button" className="mc-tickets__ghost" onClick={markResolved} disabled={ratingBusy || !selectedTicket}>Mark Resolved</button>
                  <button type="button" className="mc-tickets__primary" onClick={handleSendReply} disabled={!canReply || !reply.trim() || submitting}>
                    {submitting ? "Sending..." : "Send Reply"}
                  </button>
                </div>
              </section>
            </>
          ) : (
            <div className="mc-tickets__empty mc-tickets__empty--detail">Select a ticket to view it.</div>
          )}
        </main>
      </section>

      {createOpen ? (
        <div className="mc-tickets__modalBackdrop">
          <div className="mc-tickets__modal">
            <div className="mc-tickets__modalHead">
              <div>
                <h2>Create support ticket</h2>
                <p>Log an MC support request and track replies here.</p>
              </div>
              <button type="button" onClick={() => !createBusy && setCreateOpen(false)}>Close</button>
            </div>

            <div className="mc-tickets__formGrid">
              <label>
                <span>Area</span>
                <select value={createForm.area} onChange={(e) => setCreateForm((p) => ({ ...p, area: e.target.value, topic: "" }))}>
                  {Object.keys(AREA_TOPIC_OPTIONS).map((area) => <option key={area} value={area}>{area}</option>)}
                </select>
              </label>
              <label>
                <span>Priority</span>
                <select value={createForm.priority} onChange={(e) => setCreateForm((p) => ({ ...p, priority: e.target.value }))}>
                  {["Low", "Normal", "High", "Critical"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="mc-tickets__wide">
                <span>Topic</span>
                <select value={createForm.topic} onChange={(e) => setCreateForm((p) => ({ ...p, topic: e.target.value }))}>
                  <option value="">Select a topic…</option>
                  {selectedAreaTopics.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                </select>
              </label>
              <label className="mc-tickets__wide">
                <span>Description</span>
                <textarea
                  rows={6}
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Tell support what happened, what you expected, and how to reproduce it."
                />
              </label>
            </div>

            {error ? <div className="mc-tickets__error">{error}</div> : null}
            <div className="mc-tickets__modalActions">
              <button type="button" className="mc-tickets__ghost" onClick={() => !createBusy && setCreateOpen(false)}>Cancel</button>
              <button type="button" className="mc-tickets__primary" onClick={handleCreateTicket} disabled={createBusy}>
                {createBusy ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}

export default function MorpheusCountTickets() {
  return <MorpheusCountTicketsContent />;
}
